import type { SheetSpec } from './types'

// Excel worksheet-name hard limit.
const SHEET_NAME_MAX_LEN = 31

/** Sanitize a sheet name to Excel's rules: strip the disallowed characters
 *  ([ ] : * ? / \) and cap at 31 chars. A name that sanitizes to blank falls
 *  back to 'Sheet' (Excel rejects an empty worksheet name). */
function sanitizeSheetName(name: string): string {
  const cleaned = name.replace(/[[\]:*?/\\]/g, '_').slice(0, SHEET_NAME_MAX_LEN)
  return cleaned === '' ? 'Sheet' : cleaned
}

/** De-dupe a sanitized name against the names already used in this workbook.
 *  Collisions get a ` (n)` suffix (n = 2, 3, ...), with the base name
 *  truncated so the suffixed name still fits the 31-char limit. */
function dedupeSheetName(name: string, used: Set<string>): string {
  if (!used.has(name)) return name
  let n = 2
  let candidate: string
  do {
    const suffix = ` (${n})`
    candidate = name.slice(0, SHEET_NAME_MAX_LEN - suffix.length) + suffix
    n += 1
  } while (used.has(candidate))
  return candidate
}

/** Writes one SheetSpec per sheet into a single .xlsx workbook and triggers a
 *  file download. Each sheet is built as an explicit array-of-arrays (metadata
 *  rows, a blank separator, the header row, then one row per data row projected
 *  through `columns`) so that no nested object (e.g. a raw `dims` record) can
 *  ever land in a cell. */
export async function buildWorkbook(sheets: SheetSpec[], filename: string): Promise<void> {
  const XLSX = await import('xlsx')

  const wb = XLSX.utils.book_new()
  const usedNames = new Set<string>()

  for (const sheet of sheets) {
    const aoa: unknown[][] = [
      ...sheet.metadata.map(([k, v]) => [k, v]),
      [],
      sheet.columns,
      ...sheet.rows.map((row) => sheet.columns.map((c) => row[c] ?? null)),
    ]

    const ws = XLSX.utils.aoa_to_sheet([])
    XLSX.utils.sheet_add_aoa(ws, aoa, { origin: 0 })

    const name = dedupeSheetName(sanitizeSheetName(sheet.sheetName), usedNames)
    usedNames.add(name)

    XLSX.utils.book_append_sheet(wb, ws, name)
  }

  XLSX.writeFile(wb, filename)
}

/** Builds a one-cell error sheet, used to surface a failed per-pin/per-chart
 *  export inside an otherwise-successful multi-sheet workbook. */
export function errorSheet(title: string, message: string): SheetSpec {
  return {
    sheetName: title,
    metadata: [['Error', message]],
    columns: ['Error'],
    rows: [{ Error: message }],
  }
}
